import {
  VIEWR_UPDATE_SPACES_PATH,
  type ViewRUpdateSpacesRequestBody,
} from "./saveManifestRequest";
import type { ViewRUpdateRequestBody } from "./saveRequest";
import {
  editorVdbUploadUrlPrefetchPool,
  type PrefetchedVdbUploadUrl,
} from "./vdbUploadUrlPrefetch";

const EDITOR_SAVE_API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://viewrapi-876455037250.asia-northeast3.run.app";

const USE_WORKER_THRESHOLD = 100 * 1024;

export interface ViewRUpdateApiResponse {
  job_id?: string;
  jobId?: string;
  job?: string;
  id?: string;
  httpStatus?: number;
  [key: string]: unknown;
}

export const getEditorSaveAuthToken = (): string | null =>
  localStorage.getItem("firebaseToken") ||
  sessionStorage.getItem("firebaseToken");

export const getEditorSaveApiBaseUrl = (): string => EDITOR_SAVE_API_BASE_URL;

const resolveEditorSaveBearerToken = async (): Promise<string | null> => {
  const storedToken = getEditorSaveAuthToken();

  try {
    const { resolveSseToken } = await import("../sseToken");
    return (await resolveSseToken(storedToken)) || storedToken;
  } catch {
    return storedToken;
  }
};

export const prefetchEditorVdbUploadUrl = async (): Promise<boolean> => {
  const token = await resolveEditorSaveBearerToken();
  if (!token) return false;

  return editorVdbUploadUrlPrefetchPool.prefetch({
    apiBaseUrl: getEditorSaveApiBaseUrl(),
    bearerToken: token,
  });
};

export const clearEditorVdbUploadUrlPrefetch = (): void => {
  editorVdbUploadUrlPrefetchPool.clear();
};

export const getEditorVdbSaveApiConfig = async ({
  allowPrefetchedUpload = true,
}: {
  allowPrefetchedUpload?: boolean;
} = {}): Promise<{
  apiBaseUrl: string;
  bearerToken: string;
  prefetchedVdbUpload?: PrefetchedVdbUploadUrl;
}> => {
  const token = await resolveEditorSaveBearerToken();
  if (!token) {
    throw new Error("Authentication token not found. Please sign in again.");
  }

  const apiBaseUrl = getEditorSaveApiBaseUrl();
  const prefetchConfig = { apiBaseUrl, bearerToken: token };
  const prefetchedVdbUpload = allowPrefetchedUpload
    ? (editorVdbUploadUrlPrefetchPool.take(prefetchConfig) ?? undefined)
    : undefined;

  if (allowPrefetchedUpload) {
    void editorVdbUploadUrlPrefetchPool.prefetch(prefetchConfig);
  }

  return {
    apiBaseUrl,
    bearerToken: token,
    ...(prefetchedVdbUpload ? { prefetchedVdbUpload } : {}),
  };
};

export const serializeViewRUpdateRequestBody = async ({
  requestBody,
  byteLength,
  isWorkerReady,
  stringifyWithWorker,
}: {
  // legacy full save와 manifest save는 body shape이 다르지만 직렬화 정책은 같다.
  requestBody: ViewRUpdateRequestBody | ViewRUpdateSpacesRequestBody;
  byteLength: number;
  isWorkerReady: boolean;
  stringifyWithWorker: (value: unknown) => Promise<string>;
}): Promise<string> => {
  if (isWorkerReady && byteLength > USE_WORKER_THRESHOLD) {
    try {
      return await stringifyWithWorker(requestBody);
    } catch {
      return JSON.stringify(requestBody);
    }
  }

  return JSON.stringify(requestBody);
};

/**
 * Manifest save 제출 (ICD-MAIN-226).
 *
 * legacy full save와 응답 계약(202 + job_id)은 같지만 경로와 요청 schema가 다르다.
 * 같은 저장에서 두 경로를 함께 호출하지 않는다.
 */
export const submitViewRUpdateSpacesRequest = async ({
  apiFetch,
  token,
  bodyString,
}: {
  apiFetch: typeof fetch;
  token: string;
  bodyString: string;
}): Promise<ViewRUpdateApiResponse> => {
  const response = await apiFetch(VIEWR_UPDATE_SPACES_PATH, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: bodyString,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `ViewR manifest 업데이트 요청 실패: ${response.status} ${response.statusText} - ${errorText}`,
    );
  }

  const result = (await response.json()) as ViewRUpdateApiResponse;
  return {
    ...result,
    httpStatus: response.status,
  };
};

export const submitViewRUpdateRequest = async ({
  apiFetch,
  token,
  bodyString,
}: {
  apiFetch: typeof fetch;
  token: string;
  bodyString: string;
}): Promise<ViewRUpdateApiResponse> => {
  const response = await apiFetch("/conversion/update-viewr", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: bodyString,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `ViewR 업데이트 요청 실패: ${response.status} ${response.statusText} - ${errorText}`,
    );
  }

  const result = (await response.json()) as ViewRUpdateApiResponse;
  return {
    ...result,
    httpStatus: response.status,
  };
};
